// The OneTrade section's charts (pipeline RC4; brief of 7 Oct section 4a, drawing a6-account-file.html "OneTrade"): small
// multiples on one month axis (hours from 0; each month against its own pattern as bars, with the band's lines, a
// tournament month outlined and the month a decline was raised marked; people using it, against the seats where the read
// carries them; the fixtures on offer where it carries them), then the month against its own month a year before, twelve
// months against the twelve before, and the ten leagues it watched most with a sparkline each. Every number is track B's
// reading (RC2) as the usage route returns it; nothing is estimated here. The SVGs carry no text: every label is HTML, so
// none is drawn under 12px at any width.
import { useEffect, useState } from 'react';
import { records } from '../api';
import { groupTotals, monthShort, topLeagues, topTotals, type TopTotal, type CompanyReadings, type OneTradeGroupReading, type OneTradeReadingMonth } from './readings';

/** The readings behind the charts, fetched after the head draws (track A's usage route); null until they come, or where the build has no route. */
export function useCompanyReadings(storeId: string, companyId: string, anyRead: boolean): CompanyReadings | null {
  const [r, setR] = useState<CompanyReadings | null>(null);
  useEffect(() => {
    let live = true;
    setR(null); // another company's charts never stand while this one's come
    // Asked only where the read says a product has been read, so a record with nothing to draw costs no call.
    if (!anyRead) return () => { live = false; };
    records.companyReadings(storeId, companyId)
      // Track A's route answers { store, usage }; a bare reading or { readings } is taken too.
      .then((x) => { if (live) setR('usage' in x ? x.usage : 'readings' in x ? x.readings : x); })
      .catch(() => { if (live) setR(null); });
    return () => { live = false; };
  }, [storeId, companyId, anyRead]);
  return r;
}

const W = 240;
const H = 64;

function Bars({ months, value, from0 = true, band = null, label }: { months: OneTradeReadingMonth[]; value: (m: OneTradeReadingMonth) => number | null; from0?: boolean; band?: number | null; label: string }) {
  const vals = months.map(value);
  const present = vals.filter((v): v is number => v !== null);
  if (present.length === 0) return null;
  const hi = Math.max(...present, band ?? 0, 0);
  const lo = from0 ? 0 : Math.min(...present, -(band ?? 0), 0);
  const span = hi - lo || 1;
  const y = (v: number) => H - ((v - lo) / span) * H;
  const bw = W / months.length;
  return (
    <svg className="ot-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
      {band !== null ? <>
        <line className="ot-band" x1={0} x2={W} y1={y(band)} y2={y(band)} />
        <line className="ot-band" x1={0} x2={W} y1={y(-band)} y2={y(-band)} />
      </> : null}
      {!from0 ? <line className="ot-zero" x1={0} x2={W} y1={y(0)} y2={y(0)} /> : null}
      {months.map((m, i) => {
        const v = vals[i];
        if (v === null || v === undefined) return null;
        const top = Math.min(y(v), y(0));
        const h = Math.max(1, Math.abs(y(v) - y(0)));
        const cls = m.label === 'tournament' ? 'ot-bar ot-tournament' : band !== null && v <= -band ? 'ot-bar ot-below' : 'ot-bar';
        return <rect key={m.month} className={cls} x={i * bw + bw * 0.15} y={top} width={bw * 0.7} height={h} />;
      })}
    </svg>
  );
}

function Lines({ months, a, b, label }: { months: OneTradeReadingMonth[]; a: (m: OneTradeReadingMonth) => number | null; b: (m: OneTradeReadingMonth) => number | null; label: string }) {
  const va = months.map(a); const vb = months.map(b);
  const all = [...va, ...vb].filter((v): v is number => v !== null);
  if (all.length === 0) return null;
  const hi = Math.max(...all, 1);
  const x = (i: number) => (months.length === 1 ? W / 2 : (i / (months.length - 1)) * W);
  const path = (vs: Array<number | null>) => vs.map((v, i) => (v === null ? null : `${x(i)},${H - (v / hi) * (H - 2) - 1}`)).filter(Boolean).join(' ');
  return (
    <svg className="ot-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <polyline className="ot-line ot-line-b" points={path(vb)} />
      <polyline className="ot-line" points={path(va)} />
    </svg>
  );
}

export function Spark({ values }: { values: number[] }) {
  const hi = Math.max(...values, 1);
  const x = (i: number) => (values.length === 1 ? 30 : (i / (values.length - 1)) * 60);
  return (
    <svg className="ot-spark" viewBox="0 0 60 16" preserveAspectRatio="none" aria-hidden="true">
      <polyline className="ot-line" points={values.map((v, i) => `${x(i)},${15 - (v / hi) * 14}`).join(' ')} />
    </svg>
  );
}

function Multiple({ title, note, months, children }: { title: string; note?: string | null; months: OneTradeReadingMonth[]; children: React.ReactNode }) {
  return (
    <figure className="ot-multiple">
      <figcaption><b>{title}</b>{note ? <span>{note}</span> : null}</figcaption>
      {children}
      <span className="ot-axis"><span>{monthShort(months[0]!.month)}</span><span>{monthShort(months[months.length - 1]!.month)}</span></span>
    </figure>
  );
}

function Group({ g, band }: { g: OneTradeGroupReading; band: number }) {
  const months = g.months;
  if (months.length === 0) return null;
  const totals = groupTotals(g);
  const leagues = topLeagues(g, band);
  // Twelve months' top tens where the read carries them (track B, after RC7); the last month's leagues before that.
  const sports12 = topTotals(g, 'sports', band);
  const leagues12 = topTotals(g, 'leagues', band);
  const lastMonth = monthShort(months[months.length - 1]!.month);
  // A decline is named only while the last month still holds it: one raised in February and lapsed since is not raised
  // now (live, 7 Oct: SBO Bet's note said "decline raised in Feb 2026" under a headline saying it is not raised).
  const lastRead = months[months.length - 1]!;
  const raised = lastRead.label === 'decline_raised' ? lastRead : null;
  const cups = months.filter((m) => m.label === 'tournament');
  const hasSeats = months.some((m) => m.seats !== undefined && m.seats !== null);
  const hasFixtures = months.some((m) => m.fixtures !== undefined && m.fixtures !== null);
  // A chart is drawn only for a measure the read has a figure for, so no heading stands over an empty frame.
  const hasReading = months.some((m) => m.reading !== null);
  const hasUsers = months.some((m) => m.users !== null);
  return (
    <div className="ot-group">
      <div className="ot-multiples">
        <Multiple title="Hours watched" months={months}>
          <Bars months={months} value={(m) => m.hours} label="Hours watched each month, from zero" />
        </Multiple>
        {hasReading ? <Multiple title="Against its own pattern" months={months}
          note={[`the lines are ${Math.round(band * 100)}% either way`, cups.length ? `outlined: ${cups.map((m) => `${monthShort(m.month)}, ${m.tournament?.league.split('|').pop() ?? 'a tournament'}`).join('; ')}` : null, raised ? `a decline raised since ${monthShort(raised.raisedSince ?? raised.month)}` : null].filter(Boolean).join('; ')}>
          <Bars months={months} value={(m) => m.reading} from0={false} band={band} label="Each month against its own month a year before, for the games played" />
        </Multiple> : null}
        {hasFixtures ? (
          <Multiple title="Fixtures on offer" months={months}>
            <Bars months={months} value={(m) => m.fixtures ?? null} label="Fixtures on offer in its leagues each month" />
          </Multiple>
        ) : null}
        {hasUsers ? <Multiple title={hasSeats ? 'People against seats' : 'People using it'} months={months} note={hasSeats ? 'the faint line is the seats' : 'the faint line is a year before'}>
          <Lines months={months} a={(m) => m.users} b={(m) => (hasSeats ? m.seats ?? null : m.usersYearBefore)} label={hasSeats ? 'People using it each month, against the seats sold' : 'People using it each month, against a year before'} />
        </Multiple> : null}
      </div>
      {totals ? (
        <ul className="ot-totals">
          {totals.monthWords ? <li>{totals.monthWords}.</li> : null}
          {totals.twelveWords ? <li>{totals.twelveWords}.</li> : null}
        </ul>
      ) : null}
      {sports12.length ? <Top title={`The sports it watched most, twelve months to ${lastMonth}`} first="Sport" rows={sports12} /> : null}
      {leagues12.length
        ? <Top title={`The leagues it watched most, twelve months to ${lastMonth}`} first="League" rows={leagues12} />
        : leagues.length ? <Top title={`The leagues it watched most in ${lastMonth}`} first="League" rows={leagues.map((l) => ({ ...l, key: l.league }))} /> : null}
    </div>
  );
}

function Top({ title, first, rows }: { title: string; first: string; rows: Array<Pick<TopTotal, 'key' | 'name' | 'sport' | 'hours' | 'fixtures' | 'reading'> & { spark: number[] | null }> }) {
  const sparks = rows.some((r) => r.spark);
  return (
    <div className="ot-top">
      <p className="colh">{title}</p>
      <table className="ot-leagues">
        <thead><tr><th>{first}</th><th className="n">Hours</th><th className="n">Games</th><th>Against its own pattern</th>{sparks ? <th>Twelve months</th> : null}</tr></thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.key}>
              <th scope="row">{l.name}{l.sport ? <span className="ot-sport">{l.sport}</span> : null}</th>
              <td className="n" data-l="Hours">{Math.round(l.hours).toLocaleString('en-GB')}</td>
              <td className="n" data-l="Games">{l.fixtures.toLocaleString('en-GB')}</td>
              <td data-l="Against its own pattern">{l.reading ?? 'no reading'}</td>
              {sparks ? <td data-l="Twelve months">{l.spark ? <Spark values={l.spark} /> : null}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Every OneTrade group the company's links name, each with its charts; nothing where nothing has been read. */
export function OneTradeCharts({ readings }: { readings: CompanyReadings }) {
  if (readings.oneTrade.length === 0) return null;
  const band = readings.band ?? 0.1;
  return <div className="ot-charts">{readings.oneTrade.map((g) => <Group key={g.groupId} g={g} band={band} />)}</div>;
}
